import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CompanyOwnerUsersSection } from "@/components/companies/CompanyOwnerUsersSection";
import { Button } from "@/components/ui/button";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { deleteCompany, fetchCompanyById, getCompanyById } from "@/lib/admin/companiesStore";
import { companyFormErrorMessage } from "@/lib/admin/companyForm";
import { formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Building2,
  Calendar,
  Coins,
  Edit,
  Globe,
  Link2,
  Loader2,
  Mail,
  MapPin,
  Palette,
  Phone,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { format, isValid, parseISO } from "date-fns";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  const display = value.trim() || "—";
  return (
    <div className="flex gap-3 py-3 border-b border-border/40 last:border-0">
      <div className="shrink-0 mt-0.5 text-muted-foreground">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground mt-1 break-words">{display}</p>
      </div>
    </div>
  );
}

function DetailSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-primary shrink-0" />
        <h3 className="font-display font-semibold text-foreground text-sm">{title}</h3>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export default function CompanyView() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "company_can_view");
  const canUpdate = can(user, "company_can_update");
  const canDelete = can(user, "company_can_delete");

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let mounted = true;
    void (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        await fetchCompanyById(id);
        if (mounted) refresh();
      } catch (e) {
        console.error("[CompanyView] Load failed", e);
        if (mounted) {
          if (isAxiosError(e) && e.response?.status === 404) setNotFound(true);
          else toast.error(companyFormErrorMessage(e));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, refresh]);

  const company = useMemo(() => (id ? getCompanyById(id) : undefined), [id, version]);

  const runDelete = async () => {
    if (!company || isDeleting) return;
    setIsDeleting(true);
    try {
      const r = await deleteCompany(company.id);
      toast.success(r.message);
      setConfirmDelete(false);
      navigate("/companies", { replace: true });
    } catch (e) {
      console.error("[CompanyView] Delete failed", e);
      toast.error(companyFormErrorMessage(e));
    } finally {
      setIsDeleting(false);
    }
  };

  if (!canView) {
    return (
      <PermissionDenied
        title="Company access required"
        message="You don’t have permission to view companies. Ask an administrator to grant company_can_view."
      />
    );
  }

  if (!id) {
    return (
      <Layout title="Company">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing company id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Company">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          Loading company…
        </div>
      </Layout>
    );
  }

  if (notFound || !company) {
    return (
      <Layout title="Company">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Company not found.</div>
          <Button variant="glass" className="gap-2" asChild>
            <Link to="/companies">
              <ArrowLeft className="w-4 h-4" />
              Back to companies
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const title = company.name.trim() || "Company";
  const isActive = company.status === "active";

  return (
    <Layout title={title}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Link to="/companies">
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground truncate">{title}</h1>
              <p className="text-sm text-muted-foreground mt-1 font-mono truncate">{company.slug}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdate ? (
              <Link to={`/companies/${company.id}/edit`}>
                <Button variant="glass" className="gap-2">
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </Link>
            ) : null}
            {canDelete ? (
              <Button
                variant="ghost"
                className="gap-2 text-destructive hover:bg-destructive/10"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            ) : null}
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-border/50 bg-muted/30">
              {company.logo ? (
                <img src={company.logo} alt="" className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-8 w-8 text-muted-foreground/40" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border",
                  isActive
                    ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-400 border-emerald-500/25"
                    : "bg-muted/50 text-muted-foreground border-border/60",
                )}
              >
                {isActive ? "Active" : "Inactive"}
              </span>
              <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/30 px-2.5 py-0.5 text-xs font-medium text-foreground font-mono">
                {company.slug}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DetailSection title="Company profile" icon={Building2}>
            <InfoRow icon={Building2} label="Legal / display name" value={company.name} />
            <InfoRow icon={Link2} label="Slug" value={company.slug} />
            <InfoRow icon={Globe} label="Domain" value={company.domain ?? ""} />
            <InfoRow icon={Mail} label="Email" value={company.email ?? ""} />
            <InfoRow icon={Phone} label="Phone" value={company.phone ?? ""} />
            <InfoRow icon={MapPin} label="Address" value={company.address ?? ""} />
            <InfoRow
              icon={Coins}
              label="Base Currency"
              value={company.currency ? `${company.currency.code} (${company.currency.symbol}) — ${company.currency.name}` : "PKR (Rs.) — Pakistani Rupee"}
            />
            <div className="flex gap-3 py-3 border-b border-border/40 last:border-0">
              <div className="shrink-0 mt-0.5 text-muted-foreground">
                <Palette className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Brand Primary Color</p>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className="h-4 w-4 rounded-full border border-black/10 shadow-xs shrink-0"
                    style={{ backgroundColor: company.primaryColor || "#f59e0b" }}
                  />
                  <span className="text-sm font-mono text-foreground uppercase">
                    {company.primaryColor || "#f59e0b"}
                  </span>
                </div>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Record" icon={Calendar}>
            <InfoRow
              icon={Calendar}
              label="Created"
              value={isValid(parseISO(company.createdAt)) ? format(parseISO(company.createdAt), "PP") : company.createdAt}
            />
            <InfoRow icon={Calendar} label="Last updated" value={formatCatalogAuditDate(company.updatedAt)} />
            {company.logo ? (
              <div className="pt-2">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Logo URL</p>
                <p className="text-xs text-muted-foreground mt-1 break-all">{company.logo}</p>
              </div>
            ) : null}
          </DetailSection>
        </div>

        <CompanyOwnerUsersSection users={company.companyAssignedUsers} />

        <ConfirmAlertDialog
          open={confirmDelete}
          onOpenChange={(o) => !o && setConfirmDelete(false)}
          title="Delete this company?"
          description={
            <>
              Are you sure you want to delete <strong className="text-foreground font-semibold">{company.name}</strong>? It must not be
              linked to any user accounts. This cannot be undone.
            </>
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void runDelete()}
        />
      </div>
    </Layout>
  );
}
